// src/components/AboutDialogContent.tsx
/**
 * About Dialog Content Component
 * Displays app information, features overview, and copyright notice
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../types/redux';

// App version - keep in sync with package.json
const APP_VERSION = '0.7.2';

export const AboutDialogContent: React.FC = () => {
  const preferences = useSelector((state: RootState) => state.preferences);
  
  return (
    <div className="space-y-6">
      {/* Header with Logo and Version */}
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
          preferences.theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'
        }`}>
          <span className="text-white font-bold text-2xl">NL</span>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Number Line
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Version {APP_VERSION}
          </p>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-700 dark:text-gray-300">
        An interactive single and double number line tool designed for maths teaching, 
        suitable for Key Stage 1 through Key Stage 5.
      </p>

      {/* Key Features */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Key Features
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Interactive number line with zoom and pan</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Double number lines with customisable scale factors</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Jump indicators, measurements, and annotations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Mathematical expressions and constants (π, e, φ, and more)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Share scenes via URL</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Export to PNG</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Works offline as an installable app</span>
          </li>
        </ul>
      </div>

      {/* Copyright */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          © {new Date().getFullYear()} Tim Dolan. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AboutDialogContent;
